import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Parallax, ParallaxLayer } from '@react-spring/parallax';
import { useRouter } from 'next/router';
// Types
import { GetAllHomePageData_featuredPpvEvents } from 'api/homePage/types/GetAllHomePageData';
// Components
import Heroshot from './Heroshot/Heroshot';
import FeaturedStreamsCarousel from './FeaturedStreamsCarousel/FeaturedStreamsCarousel';
import MainPageContentSection from './EventsCta/VideoBackgroundSection';
import DownloadAppSection from './GetAppCta/GetApp/DownloadAppSection';
import PhoneAppDownloadCta from './GetAppCta/GetPhonesCta/GetPhonesCta';
import ImageGridCarousel from './ImageGridCarousel/ImageGridCarousel';
import CardLayout from './MainCards/MainCardsLayout/MainCardsLayout';
// Hooks
import { useAppContext, useGetCurrUser } from 'hooks';
// Layouts
import Footer from 'layouts/Footer/Footer';
// Constants
import { DASHBOARD } from 'constants/routes';
// Helpers
import { getSlideContent } from './helpers/SlideHelper';
// Styles
import styles from './HomePage.module.scss';

interface DesktopHomePageProps {
  banners: any[];
  cardsData: any[];
  featuredStreams: GetAllHomePageData_featuredPpvEvents[];
}

const DesktopHomePage = ({
  banners,
  cardsData,
  featuredStreams,
}: DesktopHomePageProps) => {
  const [cardFactor, setCardFactor] = useState(1);
  const [featuredStreamsFactor, setFeaturedStreamsFactor] = useState(0);
  const { push } = useRouter();
  const { setSignUpModalVisibility, setSignUpStep } = useAppContext();

  const { data: currentUser } = useGetCurrUser();
  const isLoggedIn = Boolean(currentUser?.me?.id);

  const onButtonClick = useCallback(() => {
    if (isLoggedIn) {
      push(DASHBOARD);
    } else {
      setSignUpStep(0);
      setSignUpModalVisibility(true);
    }
  }, [isLoggedIn, push, setSignUpModalVisibility, setSignUpStep]);

  useEffect(() => {
    const calculateLayout = () => {
      const viewportWidth = window.innerWidth;
      const isBentoColumn = viewportWidth <= 1024;

      if (isBentoColumn) {
        setCardFactor(2);
      } else {
        setCardFactor(1);
      }
    };

    calculateLayout();
    window.addEventListener('resize', calculateLayout);

    return () => window.removeEventListener('resize', calculateLayout);
  }, []);

  const hasFeaturedStreams = Boolean(featuredStreams?.length);
  useEffect(() => {
    if (hasFeaturedStreams) {
      setFeaturedStreamsFactor(0.6);
    } else {
      setFeaturedStreamsFactor(0);
    }
  }, [hasFeaturedStreams]);

  const parallaxChildren = useMemo(
    () =>
      [
        {
          key: 'heroshot',
          children: <Heroshot banners={banners} />,
          offset: 0,
          speed: 0.1,
          factor: 1,
        },
        ...(hasFeaturedStreams
          ? [
              {
                key: 'featured-streams',
                children: (
                  <div
                    style={{
                      height: '50vh',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <FeaturedStreamsCarousel
                      featuredStreams={featuredStreams || []}
                    />
                  </div>
                ),
                offset: 1,
                speed: 0.5,
                factor: featuredStreamsFactor,
              },
            ]
          : []),
        {
          key: 'card-layout',
          children: (
            <CardLayout cards={cardsData} onButtonClick={onButtonClick} />
          ),
          offset: 1 + featuredStreamsFactor,
          speed: 0.5,
          factor: cardFactor,
        },
        {
          key: 'phone-app-download-cta',
          children: <PhoneAppDownloadCta />,
          offset: 2 + featuredStreamsFactor,
          speed: 0.1,
          factor: 1,
        },
        {
          key: 'main-page-content-1',
          children: <MainPageContentSection {...getSlideContent(1, true)} />,
          offset: 3 + featuredStreamsFactor,
          speed: 0.2,
          factor: 1,
          sticky: {
            start: 3 + featuredStreamsFactor + 0.1,
            end: 3 + featuredStreamsFactor + 1,
          },
        },
        {
          key: 'main-page-content-2',
          children: <MainPageContentSection {...getSlideContent(2, true)} />,
          offset: 4 + featuredStreamsFactor,
          speed: 0.3,
          factor: 1,
          sticky: {
            start: 4 + featuredStreamsFactor,
            end: 4 + featuredStreamsFactor + 1,
          },
        },
        {
          key: 'main-page-content-3',
          children: <MainPageContentSection {...getSlideContent(3, true)} />,
          offset: 5 + featuredStreamsFactor,
          speed: 0.4,
          factor: 1,
          sticky: {
            start: 5 + featuredStreamsFactor,
            end: 5 + featuredStreamsFactor + 1,
          },
        },
        {
          key: 'main-page-content-4',
          children: <MainPageContentSection {...getSlideContent(4, true)} />,
          offset: 6 + featuredStreamsFactor,
          speed: 0.5,
          factor: 1,
          sticky: {
            start: 6 + featuredStreamsFactor,
            end: 6 + featuredStreamsFactor + 1,
          },
        },
        {
          key: 'image-grid-carousel',
          children: (
            <ImageGridCarousel imageCount={16} onButtonClick={onButtonClick} />
          ),
          offset: 7 + featuredStreamsFactor,
          speed: 0.6,
          factor: 1,
          sticky: {
            start: 7 + featuredStreamsFactor,
            end: 7 + featuredStreamsFactor + 1,
          },
        },
        {
          key: 'download-app-section',
          children: (
            <>
              <DownloadAppSection />
              <Footer />
            </>
          ),
          offset: 8 + featuredStreamsFactor,
          speed: 0.7,
          factor: 1,
          sticky: {
            start: 8 + featuredStreamsFactor,
            end: 8 + featuredStreamsFactor,
          },
        },
      ].filter(Boolean),
    [
      hasFeaturedStreams,
      featuredStreams,
      cardFactor,
      featuredStreamsFactor,
      cardsData,
      banners,
      onButtonClick,
    ]
  );

  return (
    <Parallax
      key={`parallax-${parallaxChildren.length}`}
      className={styles.parallaxContainer}
      pages={parallaxChildren.length}
    >
      {parallaxChildren.map((child) => (
        <ParallaxLayer
          key={child.key}
          offset={child.offset}
          speed={child.speed}
          factor={child.factor}
          sticky={child.sticky}
        >
          {child.children}
        </ParallaxLayer>
      ))}
    </Parallax>
  );
};

export default DesktopHomePage;
